import React from 'react';
import { Outlet } from 'react-router-dom';
import { BrandThemeProvider, useBrandTheme, useBrandCSSVariables } from '../../contexts/BrandThemeContext';
import env from '../../config/env';
import './styles.scss';

const LayoutContent: React.FC = () => {
  const { theme } = useBrandTheme();
  const cssVariables = useBrandCSSVariables();

  return (
    <div className="tw-min-h-screen tw-flex tw-flex-col" style={cssVariables}>
      {/* Mobile banner with background image - Only visible on mobile */}
      <div className="md:tw-hidden tw-relative tw-h-[200px] tw-w-full">
        <div className="tw-absolute tw-inset-0">
          <img 
            src={theme.formBackgroundImage} 
            alt={`${theme.displayName} Background`} 
            className="tw-w-full tw-h-full tw-object-cover" 
          />
        </div>
        
        {/* Mobile rectangles container - positioned over the banner */}
        <div className="tw-absolute tw-top-5 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-10 tw-flex">
          {/* Logo rectangle */}
          <a
            href={env.HOME_DIPROPNEU_URL || '#'}
            className="tw-flex tw-items-center tw-justify-center tw-border tw-border-r-0 tw-shadow-lg tw-no-underline"
            style={{
              width: '20vw',
              maxWidth: '120px',
              minWidth: '70px',
              height: '60px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '8px 0 0 8px'
            }}
          >
            <img 
              src={theme.logo} 
              alt={`${theme.displayName} Logo`} 
              className="tw-object-contain tw-w-full tw-h-full tw-p-2"
            />
          </a>
          
          {/* Phone rectangle */}
          <div 
            className="tw-flex tw-items-center tw-justify-center tw-border tw-shadow-lg tw-text-white tw-font-bold tw-text-center"
            style={{
              width: '35vw',
              maxWidth: '200px',
              minWidth: '140px',
              height: '60px',
              backgroundColor: theme.colors.primary,
              backdropFilter: 'blur(10px)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '0 8px 8px 0',
              fontSize: 'clamp(1rem, 4vw, 1.5rem)',
              lineHeight: '1.2'
            }}
          >
            {theme.phoneNumber}
          </div>
        </div>
      </div>

      {/* Main content with two columns */}
      <div className="tw-flex tw-flex-1">
        {/* Left side - Background image (58% on desktop) - Hidden on mobile */}
        <div className="tw-w-full lg:tw-w-1/2 xl:tw-w-[58.33%] tw-relative tw-hidden md:tw-block">
          <div className="tw-absolute tw-inset-0">
            <img 
              src={theme.formBackgroundImage} 
              alt={`${theme.displayName} Background`} 
              className="tw-w-full tw-h-full tw-object-cover" 
            />
          </div>
          
          {/* Top rectangles container - Exact structure from login template - Desktop only */}
          <div className="tw-absolute tw-top-0 tw-left-0 tw-w-full">
            <div className="tw-h-[150px] tw-w-4/5 tw-absolute tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-10 tw-flex">
              {/* Logo rectangle */}
              <a
                href={env.HOME_DIPROPNEU_URL || '#'}
                className="tw-w-1/3 tw-h-[150px] tw-flex tw-items-center tw-justify-center tw-border tw-border-r-0 tw-shadow-lg tw-no-underline"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '12px 0 0 12px'
                }}
              >
                <img 
                  src={theme.logo} 
                  alt={`${theme.displayName} Logo`} 
                  className="tw-object-contain"
                  style={{ maxWidth: '70%', maxHeight: '100px' }}
                />
              </a>
              
              {/* Text rectangle with phone number */}
              <div 
                className="tw-flex-grow tw-h-[150px] tw-flex tw-items-center tw-justify-center tw-border tw-shadow-lg tw-text-white"
                style={{
                  backgroundColor: theme.colors.primary,
                  backdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '0 12px 12px 0'
                }}
              >
                <div className="tw-text-center">
                  <p className="tw-mb-0 tw-font-bold tw-text-base">Une question concernant votre souscription ?</p>
                  <p className="tw-mb-0 tw-font-bold tw-text-4xl">{theme.phoneNumber}</p>
                  <p className="tw-mb-0 tw-text-base">De 9h à 12h30 et de 14h à 17h30 (appel gratuit)</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Form content (42% on desktop, 100% on mobile) */}
        <div className="tw-w-full lg:tw-w-1/2 xl:tw-w-[41.67%] tw-flex tw-flex-col tw-bg-white">
          <main className="tw-flex-1 tw-overflow-y-auto tw-p-6 tw-flex tw-items-center tw-justify-center">
            <div className="tw-w-full tw-max-w-2xl">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      
      {/* Footer - Full width with exact height from login template */}
      <footer 
        className="tw-py-2 lg:tw-py-6 tw-text-white tw-relative tw-flex-shrink-0"
        style={{ backgroundColor: theme.colors.primary }}
      >
        {/* Image garantie à cheval sur les sections - Taille exacte du template login - Hidden on mobile */}
        <div className="tw-hidden md:tw-block tw-absolute tw-left-5" style={{ bottom: '2px' }}>
          <div className="tw-w-[250px] tw-h-[250px] tw-flex tw-items-end tw-justify-center">
            <img src={theme.warrantyCard} alt="Garantie" className="tw-w-full tw-h-full tw-object-contain" />
          </div>
        </div>
        
        <div className="tw-pr-6 tw-h-full tw-flex tw-items-center tw-justify-end">
          {/* Liens footer repositionnés - Mobile responsive */}
          <div className="tw-text-base tw-flex tw-flex-col md:tw-flex-row tw-items-end md:tw-items-center tw-gap-4">
            <a href={`${env.DECLARATION_SAV_URL}/legal_notice` || '#'} target="_blank" rel="noopener noreferrer" className="tw-text-white tw-no-underline hover:tw-text-gray-200">
              Mentions légales
            </a>
            <a href={`${env.DECLARATION_SAV_URL}/privacy_policy` || '#'} target="_blank" rel="noopener noreferrer" className="tw-text-white tw-no-underline hover:tw-text-gray-200">
              Politique de confidentialité
            </a>
            <a href={`${env.DECLARATION_SAV_URL}/general_terms_and_conditions` || '#'} target="_blank" rel="noopener noreferrer" className="tw-text-white tw-no-underline hover:tw-text-gray-200">
              Conditions générales de vente
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export const Layout: React.FC = () => {
  return (
    <BrandThemeProvider>
      <LayoutContent />
    </BrandThemeProvider>
  );
};